import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '@/lib/toast';
import { trackCheckoutStarted } from '@/lib/upgradeTracking';
import { Check, RefreshCw, ArrowLeft, Loader2 } from 'lucide-react';
import liliLogo from '@/assets/f04a5da8d0cb08a2adb513177bda387ef12a0781.png';
import { usePlanStore } from '@/stores';
import { billingApi } from '@/services/api/billingApi';
import { extractError } from '@/lib/extractError';
import { returnAfterUpgrade } from '@/lib/upgradeReturn';
import type { PlanCard } from '@/types';
import './Paywall.css';

const BADGE_LABEL: Record<NonNullable<PlanCard['badge']>, string> = {
  best_value: 'Best value',
  popular: 'Popular',
};

/** Stable id so the activation toast is updated in place (loading → done)
 *  instead of stacking a second toast on top of the first. */
const ACTIVATION_TOAST = 'subscription-activation';

interface PaywallProps {
  /** Heading copy — defaults to the app-lock message. */
  title?: string;
  subtitle?: string;
  /** When provided, renders a "back" affordance (used by the onboarding paywall). */
  onBack?: () => void;
}

/**
 * Full-screen subscription wall. Shown whenever the user lacks an active
 * subscription — both as the app-wide gate ([[SubscriptionGate]]) and right
 * before the onboarding automation trigger. Collects payment via the existing
 * Stripe Checkout redirect (no embedded card form), then the BE webhook flips
 * the subscription to active and the gate re-hydrates on return.
 *
 * Layout is a two-panel membership moment: a deep brand panel carrying the
 * thesis, and a paper panel where the price is the hero. The brand panel is the
 * constant; the paper panel swaps between plan selection and the post-checkout
 * activation states. All styling lives in Paywall.css — see the note there for
 * why this component doesn't rely on arbitrary Tailwind utilities.
 */
export function Paywall({
  title = 'Activate your subscription',
  subtitle = 'One active plan unlocks LiLi and keeps your automation running.',
  onBack,
}: PaywallProps) {
  const { catalog, hydrated, loading, hydrate, pollUntilActive } = usePlanStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkoutId, setCheckoutId] = useState<string | null>(null);

  // Seed from the URL at mount so the activation view shows on the very first
  // render (before the poll effect runs) instead of flashing the plan cards.
  const returnedFromCheckout = searchParams.get('status') === 'success';
  const [activating, setActivating] = useState(returnedFromCheckout);
  const [activationTimedOut, setActivationTimedOut] = useState(false);
  // Guards a state update after the await if the gate already swapped the app
  // in (Paywall unmounts the moment `subscription` reads active).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Self-sufficient: hydrate the catalog if we were mounted before the store
  // was populated (e.g. the onboarding paywall).
  useEffect(() => {
    if (!hydrated) void hydrate();
  }, [hydrated, hydrate]);

  // Returning from Stripe Checkout (full reload): the BE webhook that flips the
  // subscription to active routinely lags the redirect, so a single re-hydrate
  // reads "inactive" and strands the paying user on the paywall. Poll until it
  // lands — the gate swaps to the app automatically once `subscription` reads
  // active. If the poll window runs out we surface a recoverable "still
  // activating" state (not the plan cards) so the user is never asked to pay
  // again and can retry once the webhook catches up.
  const runActivation = useCallback(async () => {
    setActivationTimedOut(false);
    setActivating(true);
    toast.loading('Payment received. Activating your subscription…', { id: ACTIVATION_TOAST });
    const active = await pollUntilActive();
    // Before the mounted check: the gate usually unmounts us as soon as the plan
    // reads active, and the router's navigate still works after that.
    if (active) void returnAfterUpgrade(navigate);
    if (!mountedRef.current) return;
    if (active) {
      // Gate is about to unmount this component; just resolve the toast.
      toast.success('Subscription activated. Welcome to LiLi!', { id: ACTIVATION_TOAST });
    } else {
      setActivating(false);
      setActivationTimedOut(true);
      toast.error('Your payment went through, but activation is taking longer than usual.', {
        id: ACTIVATION_TOAST,
      });
    }
  }, [pollUntilActive, navigate]);

  useEffect(() => {
    if (!returnedFromCheckout) return;
    // Strip `status` so a manual refresh of the paywall doesn't replay the
    // activation flow. The mount-time read above already captured it.
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    setSearchParams(next, { replace: true });
    void runActivation();
    // Run once for the status present at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelect = async (p: PlanCard) => {
    if (!p.purchasable || checkoutId) return;
    setCheckoutId(p.id);
    try {
      const { url } = await billingApi.checkout(p.id);
      trackCheckoutStarted(p);
      window.location.href = url;
    } catch (err) {
      toast.error(extractError(err, 'Could not start checkout. Please try again.'));
      setCheckoutId(null);
    }
  };

  const plans = catalog
    .filter(p => p.visible && p.purchasable)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // ─── Post-checkout activation states ───────────────────────────────────────
  // Shown in the paper panel instead of the plan cards so a paying user is never
  // asked to choose/pay again while the webhook settles (or while they retry).
  if (activating) {
    return (
      <Shell onBack={onBack}>
        <div className="pw-col">
          <span className="pw-state-icon">
            <Loader2 className="pw-spin" size={24} />
          </span>
          <h1 className="pw-display pw-title pw-title--sm">Setting up your account</h1>
          <p className="pw-subtitle">
            Your payment went through. We&rsquo;re waiting on confirmation from our payment
            provider. This usually takes a few seconds, so keep this tab open.
          </p>
        </div>
      </Shell>
    );
  }

  if (activationTimedOut) {
    return (
      <Shell onBack={onBack}>
        <div className="pw-col">
          <span className="pw-state-icon">
            <Check size={24} />
          </span>
          <h1 className="pw-display pw-title pw-title--sm">Almost there</h1>
          <p className="pw-subtitle">
            Your payment was received, but the subscription hasn&rsquo;t switched on yet. This
            normally clears in a moment, and there&rsquo;s no need to pay again.
          </p>
          <button className="pw-btn pw-btn--ink pw-btn--auto" onClick={() => void runActivation()}>
            <RefreshCw size={16} />
            Check again
          </button>
          <p className="pw-note">
            Still stuck after a few tries? Contact support and your payment stays safe.
          </p>
        </div>
      </Shell>
    );
  }

  // ─── Plan selection ────────────────────────────────────────────────────────
  return (
    <Shell onBack={onBack}>
      <div className="pw-col">
        <span className="pw-eyebrow">Membership</span>
        <h1 className="pw-display pw-title">{title}</h1>
        <p className="pw-subtitle">{subtitle}</p>

        {!hydrated && loading ? (
          <div className="pw-skeleton" />
        ) : plans.length === 0 ? (
          <div className="pw-empty">
            <p>No plans are available right now. Refresh, or try again in a moment.</p>
            <button className="pw-btn pw-btn--ink pw-btn--auto" onClick={() => hydrate()}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        ) : (
          <div className={`pw-plans${plans.length > 1 ? ' pw-plans--multi' : ''}`}>
            {plans.map(plan => (
              <PlanTile
                key={plan.id}
                plan={plan}
                busy={checkoutId === plan.id}
                onSelect={() => onSelect(plan)}
              />
            ))}
          </div>
        )}

        <p className="pw-note">
          Already paid?{' '}
          <button className="pw-link" onClick={() => hydrate()}>
            Refresh status
          </button>
        </p>
      </div>
    </Shell>
  );
}

/**
 * A single plan as a "card you carry" — name as a quiet eyebrow, the price set
 * large in the display face as the hero, then what's included. The best-value
 * plan earns the only saturated treatment on the surface.
 */
function PlanTile({
  plan,
  busy,
  onSelect,
}: {
  plan: PlanCard;
  busy: boolean;
  onSelect: () => void;
}) {
  const isBest = plan.badge === 'best_value';
  return (
    <div className={`pw-tile${isBest ? ' pw-tile--best' : ''}`}>
      {plan.badge && (
        <span className={`pw-badge${isBest ? ' pw-badge--best' : ''}`}>
          {BADGE_LABEL[plan.badge]}
        </span>
      )}

      <span className="pw-plan-name">{plan.name}</span>

      <div className="pw-price">
        <span className="pw-display pw-price__amount">{plan.price.display}</span>
        {plan.price.interval && (
          <span className="pw-price__interval">
            {plan.type === 'subscription'
              ? `/${plan.price.interval.replace(/[_-]+/g, ' ')}`
              : plan.price.interval.replace(/[_-]+/g, ' ')}
          </span>
        )}
      </div>

      <p className="pw-plan-desc">{plan.description}</p>

      <div className="pw-rule" />

      <ul className="pw-features">
        {plan.features.map((feature, index) => (
          <li key={index}>
            <Check size={18} strokeWidth={2.5} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        className={`pw-btn ${isBest ? 'pw-btn--accent' : 'pw-btn--ink'}`}
        disabled={busy}
        onClick={onSelect}
      >
        {busy ? 'Redirecting…' : (plan.ctaLabel ?? 'Choose plan')}
      </button>
    </div>
  );
}

/**
 * Two-panel membership shell. Left: the constant deep brand panel carrying the
 * thesis (≥1024px only). Right: a paper panel that scrolls and holds whatever
 * the current step renders. On small screens the brand panel collapses to a
 * compact masthead above the content.
 */
function Shell({ children, onBack }: { children: React.ReactNode; onBack?: () => void }) {
  return (
    <div className="pw-root">
      {/* Brand panel — the thesis */}
      <aside className="pw-aside">
        <div aria-hidden className="pw-glow pw-glow--violet" />
        <div aria-hidden className="pw-glow pw-glow--pink" />

        <div className="pw-aside__inner">
          <div className="pw-brandmark">
            <img src={liliLogo} alt="LiLi" className="pw-brandmark__icon" />
            <span className="pw-display pw-brandmark__word">LiLi</span>
          </div>

          <div>
            <h2 className="pw-display pw-thesis">
              Your voice,
              <br />
              published daily.
            </h2>
            <p className="pw-thesis-sub">
              LiLi turns your expertise into a steady stream of authentic posts across every
              channel, so you stay visible without living in your feed.
            </p>

            <ul className="pw-promises">
              {[
                'Written in your own voice',
                'Scheduled across every platform',
                'Your whole presence, one workspace',
              ].map((line, i) => (
                <li key={i}>
                  <Check size={16} strokeWidth={2.5} />
                  {line}
                </li>
              ))}
            </ul>
          </div>

          <p className="pw-aside__foot">Cancel anytime · Secure checkout via Stripe</p>
        </div>
      </aside>

      {/* Paper panel — the current step */}
      <main className="pw-main">
        {onBack && (
          <div className="pw-topbar">
            <button className="pw-back" onClick={onBack}>
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
        )}

        {/* Mobile masthead — the brand panel collapses to this on small screens. */}
        <div className="pw-mobile-brand pw-brandmark">
          <img src={liliLogo} alt="LiLi" className="pw-brandmark__icon" />
          <span className="pw-display pw-brandmark__word">LiLi</span>
        </div>

        <div className="pw-stage">{children}</div>
      </main>
    </div>
  );
}
